import { Button } from '@design-system/button';
import { useGetBankInfo } from './services/get-bank-info';
import { ArrowLeft } from 'iconsax-react';
import { useNavigate } from 'react-router-dom';
import { createRoute, PATHS } from '@src/core/router/paths';
import Loader from '@shared/components/reusables/loader';
import type { FormikProps } from 'formik';
import { Form, Formik } from 'formik';
import {
  eWalletValidationSchema,
  generateInitialEWalletValues,
  generateInitialValuesForBank,
  validationSchema,
} from './edit-bank-info/helper';
import FinancialDetailsEdit from './edit-bank-info/financial-details-edit';
import BankAddressEdit from './edit-bank-info/bank-address-edit';
import { useEditBankInfo } from './services/edit-bank-info';
import { useCreateBankInfo } from './services/create-bank-info';
import EWalletEdit from './edit-bank-info/e-wallet-edit';
import { useEWallet } from './services/get-e-wallet';
import { useEditEWallet } from './services/edit-e-wallet';
import { useCreateEWallet } from './services/create-e-wallet';
import { useRef } from 'react';
import type {
  IInitialBankValues,
  IInitialEWalletValues,
} from './edit-bank-info/interfaces';

const EditBankInfo = () => {
  const navigate = useNavigate();

  const navigateToBankInfo = () => {
    navigate(createRoute(PATHS.SETTINGS_BANK_INFO));
  };
  const formBankInfoRef = useRef<FormikProps<IInitialBankValues>>(null);
  const formEWalletRef = useRef<FormikProps<IInitialEWalletValues>>(null);

  const { data: bankInfoData, isLoading } = useGetBankInfo();
  const { data: eWalletData, isLoading: walletLoading } = useEWallet();

  const isEditMode = bankInfoData?.results[0];
  const { mutateAsync: editBankMutate, isPending: isEditBankPending } =
    useEditBankInfo();
  const { mutateAsync: editWalletMutate, isPending: isEditWalletPending } =
    useEditEWallet();

  const { mutateAsync: createBankMutate, isPending: isCreateBankPending } =
    useCreateBankInfo();
  const { mutateAsync: createEWalletMutate, isPending: isCreateWalletPending } =
    useCreateEWallet();

  const handleSharedSubmit = async () => {
    if (formBankInfoRef.current?.dirty) {
      await formBankInfoRef.current.submitForm();
    }
    if (formEWalletRef.current?.dirty) {
      await formEWalletRef.current.submitForm();
    }
  };

  const handleBankInfoSubmit = async (values: IInitialBankValues) => {
    if (isEditMode) {
      await editBankMutate({ ...values, id: bankInfoData?.results[0].id }).then(
        navigateToBankInfo
      );
    } else {
      await createBankMutate(values).then(navigateToBankInfo);
    }
  };

  const handleEWalletSubmit = async (values: IInitialEWalletValues) => {
    const id = eWalletData?.results[0]?.id;
    if (id) {
      editWalletMutate({
        ...values,
        id,
      }).then(navigateToBankInfo);
    } else {
      createEWalletMutate(values).then(navigateToBankInfo);
    }
  };

  return (
    <div className='relative bg-primary-30'>
      <div className='flex justify-between bg-white items-center p-6'>
        <h1 className='text-2xl font-semibold text-gray-1000 flex items-center'>
          <Button
            variant='primaryLink'
            size={'iconSm'}
            onClick={navigateToBankInfo}
          >
            <ArrowLeft />
          </Button>
          <p className='text-2xl font-semibold text-gray-1000'>
            {isEditMode ? 'Edit' : 'Add'} Bank Info
          </p>
        </h1>
      </div>
      {isLoading || walletLoading ? (
        <Loader />
      ) : (
        <div className='p-5'>
          <Formik
            key={'bank_form'}
            initialValues={generateInitialValuesForBank(
              bankInfoData?.results[0]
            )}
            innerRef={formBankInfoRef}
            validationSchema={validationSchema}
            onSubmit={handleBankInfoSubmit}
          >
            <Form className='h-full flex flex-col justify-between'>
              <FinancialDetailsEdit />
              <BankAddressEdit />
            </Form>
          </Formik>
          {/* E-Wallet Form */}
          <Formik
            key={'wallet_form'}
            initialValues={generateInitialEWalletValues(
              eWalletData?.results?.[0]
            )}
            innerRef={formEWalletRef}
            validationSchema={eWalletValidationSchema}
            onSubmit={handleEWalletSubmit}
            validateOnChange={true}
            validateOnBlur={false}
          >
            <Form className='h-full flex flex-col justify-between'>
              <EWalletEdit />
            </Form>
          </Formik>
        </div>
      )}
      <div className='bg-white border-t-2 z-50 border-pale-border inset-x-0 bottom-0 sticky'>
        <div className='flex items-center py-4 px-5 mx-auto justify-between'>
          <Button
            variant='primaryOutline'
            onClick={() => navigate(createRoute(PATHS.SETTINGS_BANK_INFO))}
          >
            Cancel
          </Button>
          <Button
            isLoading={
              isEditBankPending ||
              isCreateBankPending ||
              isEditWalletPending ||
              isCreateWalletPending
            }
            onClick={handleSharedSubmit}
            type='submit'
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditBankInfo;
